/* eslint-disable camelcase */
import instance, { client } from "@johnpaul/jp-api-client";
import { AxiosError } from "axios";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { fetchAllResources, getApiURL, getClientCredentials } from "./config";
import { HashParams } from "@companion-core/shared/app/Interfaces/API";
import { ConfigType } from "@companion-core/shared/app/Interfaces/config";
import { ConfigResponse } from "@companion-core/shared/app/Interfaces/config";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import {
  GooglePlaceAddress,
  SearchAddressResponse,
} from "@companion-core/shared/app/Interfaces/adresses";
import { GenericError, getErrorCode } from "@companion-core/shared/app/Utils/errors";
import { formatLocation } from "@companion-core/shared/app/Utils/address";

export interface APIError {
  response: {
    status: number;
  };
}

export let hashPassword: (
  password: string,
  salt: string,
  iterations: number,
  memory: number,
  parallelism: number,
  hashLength: number
) => Promise<string>;

export function initHashPassword(
  hashPasswordFunction: (
    password: string,
    salt: string,
    iterations: number,
    memory: number,
    parallelism: number,
    hashLength: number
  ) => Promise<string>
) {
  hashPassword = hashPasswordFunction;
}

export function initApiClient(baseURL?: string) {
  instance.defaults.baseURL = baseURL ?? getApiURL();
}

export async function getHashParams({ username = "", sub = "", token = "" }) {
  const params: HashParams = {};
  if (username) params.username = username;
  if (sub) params.sub = sub;
  if (token) params.token = token;

  return (await client.auth.password.hash(params)).data;
}

/**
 * Retrieves the client token using the client credentials and returns it.
 * Must be called with a system to refresh the token.
 * @async
 * @function getClientTokenFn
 * @returns {Promise<any>} A promise that resolves to the function response.
 * @throws {Error} Throws an error if unable to retrieve the client token.
 */
export async function getClientTokenFn(): Promise<{ access_token: string }> {
  return client.auth
    .token(getClientCredentials().clientId, getClientCredentials().clientSecret, {
      grant_type: "client_credentials",
    })
    .then((response: Record<string, unknown>) => response?.data as { access_token: string })
    .catch((error: unknown) => {
      throw new Error(getErrorCode(error as AxiosError<GenericError>));
    });
}

/**
 * Asynchronously retrieves the client token used for authentication.
 * If authenticatedParam is provided, it will be used to determine the authentication status.
 * If not provided, the authentication status will be obtained from the current application state.
 * If the user is authenticated, the access token from the store is returned.
 * If the user is not authenticated or the access token is expired, a new token is requested and stored.
 *
 * @param {boolean} [authenticatedParam] - Optional parameter to indicate current authentication status.
 * @returns {Promise<string>} A Promise that resolves to the client access token.
 * @throws {Error} If there is an error during token retrieval or storage.
 */
export const getClientToken = async (authenticatedParam?: boolean): Promise<string> => {
  const authenticated = authenticatedParam ?? useAuthStore.getState().authenticated;
  const accessTokenClient = useAuthStore.getState().accessTokenClient;

  if (authenticated) {
    return accessTokenClient ?? "";
  }

  if (accessTokenClient) {
    const jwt: JwtPayload = jwtDecode(accessTokenClient);
    const isExpired = jwt.exp ? Date.now() >= jwt.exp * 1000 : false;

    if (!isExpired) {
      return accessTokenClient;
    }
  }

  try {
    const newToken = await getClientTokenFn();
    useAuthStore.getState().setAccessTokenClient(newToken.access_token);
    return newToken.access_token;
  } catch (error) {
    throw new Error(getErrorCode(error as AxiosError<GenericError>));
  }
};

/**
 * Transform password given into argon2 hashed password
 *
 * @async
 * @returns {Promise<string>} argon2 hashed password
 * @throws {Error} Throws an error if unable to hash password.
 */

export async function getHashedPassword(username: string, password: string, authenticated = false) {
  const clientToken: string = await getClientToken(authenticated);
  const params: { username: string; sub?: string; token?: string } = { username };
  if (!authenticated) {
    params.token = clientToken;
  }

  const { hash_len, m, p, salt, t } = await getHashParams(params);

  return await hashPassword(password, salt, t, m, p, hash_len);
}

export const checkPassword = async (username: string, password: string) => {
  try {
    const { clientId, clientSecret } = getClientCredentials();
    const hash = await getHashedPassword(username, password);
    const params = {
      grant_type: "password",
      username: username.toLowerCase(),
      password: hash,
    };

    await client.auth.token(clientId, clientSecret, params, {
      doNotForceExit: true,
    });

    return { success: true, hash };
  } catch (error) {
    const errorCode = getErrorCode(error as AxiosError<GenericError>);
    return { success: false, error: errorCode };
  }
};

export const authLogin = async (username: string, password: string, alreadyHashed: boolean) => {
  try {
    const { clientId, clientSecret } = getClientCredentials();
    let hashedPassword = password;

    if (!alreadyHashed) {
      hashedPassword = await getHashedPassword(username, password);
    }

    const params = {
      grant_type: "password",
      username: username.toLowerCase(),
      password: hashedPassword,
    };

    const response = await client.auth.token(clientId, clientSecret, params, {
      doNotForceExit: true,
    });
    return { data: response.data, hash: hashedPassword };
  } catch (error) {
    throw new Error(getErrorCode(error as AxiosError<GenericError>));
  }
};

export async function authLogout(): Promise<void> {
  try {
    await client.auth.revoke(
      getClientCredentials().clientId,
      getClientCredentials().clientSecret,
      ""
    );
  } catch (error) {
    throw new Error(getErrorCode(error as AxiosError<GenericError>));
  }
}

export interface AddressesResponse {
  success: boolean;
  data?: SearchAddressResponse[];
  error?: unknown;
}

export async function getAddresses(
  value: string,
  addressTypes: string | undefined,
  language: string = "fr"
): Promise<AddressesResponse> {
  try {
    const { data } = await client.addresses.v2.autocomplete({
      input: value,
      includedPrimaryTypes: addressTypes,
      languageCode: language,
    });
    return { success: true, data: data.suggestions as SearchAddressResponse[] };
  } catch (error) {
    return { success: false, error: (error as AxiosError).toJSON() };
  }
}

export interface AddressResponse {
  success: boolean;
  response?: GooglePlaceAddress;
  error?: unknown;
}

export async function getAddress(
  addressId: string,
  language: string = "fr",
  expectedPlaceLevel?: string
): Promise<AddressResponse> {
  try {
    // Fetch address detail
    const addressData = await client.addresses.v2.place(addressId, { languageCode: language });
    return {
      success: true,
      response: formatLocation(addressData.data, { expectedPlaceLevel: expectedPlaceLevel }),
    };
  } catch (error) {
    return { success: false, error: (error as AxiosError).toJSON() };
  }
}

export async function fetchPublicResource(slug: string, platform: string) {
  try {
    const { data } = await client.content.publicResources.get(slug, platform);
    return { success: true, data: data.data, slug };
  } catch (error) {
    return { success: false, error: (error as AxiosError).toJSON(), slug };
  }
}

export const fetchConfig = async (id: string, type: ConfigType): Promise<ConfigResponse> => {
  useConfigStore.getState().setFetching(true, type);
  try {
    const response = await client.content.v2.resources.get(id);
    return {
      success: true,
      config: response.data.results[0].data,
    };
  } catch (error) {
    return { success: false, error: (error as AxiosError).toJSON() };
  } finally {
    useConfigStore.getState().setFetching(false, type);
  }
};

export const reFetchAllResources = async (authenticated?: boolean) => {
  await getClientToken(authenticated);
  await fetchAllResources();
};
